// Total contract value card — wraps MetricCard with a stacked-bar
// breakdown of the value composition. Three segments: head contract,
// non-cancelled SOWs, and approved variations. The third segment
// renders only when `variationsValue !== 0` so contracts with no
// variations collapse cleanly to the two-segment view.
//
// Headline pairs the ex-GST total with an inc-GST companion ("$X ex-GST
// ($Y inc-GST)") when GST status is confirmed to apply. The inc-GST
// bracket is suppressed when GST is recorded as not applicable, or when
// GST status has not been recorded — surfacing a derived inc-GST figure
// in either case would be contractually unsafe.
//
// The total is `contractValue + sowsValue + variationsValue` — additive
// only per the locked decision. Subtractive / draw-down semantics
// deferred to a future config flag. Negative variationsValue (net
// descope) is supported — the stacked-bar primitive sizes segments by
// absolute magnitude so a red-net contract still renders inspectably.

import MetricCard from "../deliverable/MetricCard";
import StackedBarBreakdown from "../shared/StackedBarBreakdown";
import {
  formatSowCurrency,
  formatSowCurrencyAbbreviated,
} from "./formatSowCurrency";
import { gstSuffix } from "../../../../utils/gstSuffix";

interface ContractValueCardProps {
  /** Head contract value as recorded on the parent contract record. */
  contractValue: number;
  /** Aggregate value of all non-cancelled, non-archived SOWs on the contract. */
  sowsValue: number;
  /** Sum of approved, non-archived variation impacts. Defaults to 0
   *  so callers that don't yet have variation data render the
   *  two-segment view cleanly. */
  variationsValue?: number;
  gstApplies: boolean | undefined;
  /** When true, render a `▾ Show timeline` (or `▴ Hide timeline`) cue
   *  below the legend. The cue only renders when the expand callback is
   *  also supplied — a card without an expand handler stays static. */
  hasTimeline?: boolean;
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

export default function ContractValueCard({
  contractValue,
  sowsValue,
  variationsValue = 0,
  gstApplies,
  hasTimeline = false,
  expanded = false,
  onToggleExpanded,
}: ContractValueCardProps) {
  const total = contractValue + sowsValue + variationsValue;
  // Render the headline with cents (VAR-4 + brand "Numbers" rule
  // amended 2026-05-04). Aggregates from milestone payments, SOW
  // values and variation impacts can carry cents; hiding cents on the
  // headline while showing them on subordinate line items surfaces an
  // off-by-one mismatch when reconciling totals. The abbreviated
  // breakdown legend below still abbreviates per its own rule.
  const formatted = formatSowCurrency(total, gstApplies, "cents");
  const basis = gstSuffix(gstApplies);
  // Inc-GST companion line is only meaningful when the contract is
  // confirmed to attract GST. For confirmed-no-GST and unrecorded-GST
  // states the bracket is suppressed — surfacing an "× 1.10" derived
  // figure under either of those conditions would be misleading.
  const incGstFormatted =
    gstApplies === true
      ? formatSowCurrency(total * 1.1, true, "cents").value
      : null;

  const segments = [
    {
      key: "contract",
      value: contractValue,
      label: "Contract",
      colorClass: "bg-brand-500 dark:bg-brand-400",
      formattedValue: formatSowCurrencyAbbreviated(contractValue),
    },
    {
      key: "sows",
      value: sowsValue,
      label: "SOWs",
      colorClass: "bg-emerald-500 dark:bg-emerald-400",
      formattedValue: formatSowCurrencyAbbreviated(sowsValue),
    },
  ];

  if (variationsValue !== 0) {
    segments.push({
      key: "variations",
      value: variationsValue,
      label: "Variations",
      colorClass: "bg-amber-500 dark:bg-amber-400",
      formattedValue: formatSowCurrencyAbbreviated(variationsValue),
    });
  }

  const ariaLabel =
    `Total contract value ${formatted.value} ${basis}` +
    (incGstFormatted ? ` (${incGstFormatted} inc-GST)` : "") +
    `, broken down as: ` +
    `contract ${formatSowCurrencyAbbreviated(contractValue)}, ` +
    `SOWs ${formatSowCurrencyAbbreviated(sowsValue)}` +
    (variationsValue !== 0
      ? `, variations ${formatSowCurrencyAbbreviated(variationsValue)}`
      : "");

  return (
    <MetricCard
      label="Total contract value"
      value={
        <span className="tabular-nums">
          {formatted.value}{" "}
          <span
            className="text-caption font-normal text-slate-500 dark:text-slate-400"
            data-testid="contract-value-card-basis"
          >
            {basis}
          </span>
          {incGstFormatted && (
            <span
              className="ml-2 text-caption font-normal text-slate-500 dark:text-slate-400"
              data-testid="contract-value-card-inc-gst"
            >
              ({incGstFormatted} inc-GST)
            </span>
          )}
        </span>
      }
      breakdown={
        <div>
          <StackedBarBreakdown
            segments={segments}
            total={total}
            testId="contract-value-card-bar"
          />
          {hasTimeline && onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              data-testid="contract-value-timeline-toggle"
              className="mt-2 inline-flex items-center gap-1 text-caption text-brand-600 dark:text-brand-400 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm"
            >
              {expanded ? "▴ Hide timeline" : "▾ Show timeline"}
            </button>
          )}
        </div>
      }
      ariaLabel={ariaLabel}
      testId="contract-value-card"
    />
  );
}
